import React, { useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useParallaxY } from "../../hooks/useParallaxY";
import AnimatedText from "../AnimatedText";
import CreativeButton from "../CreativeButton";

const ProductShowcase: React.FC = () => {
  const navigate = useNavigate();
  const showcaseSectionRef = useRef<HTMLElement>(null);
  const showcaseBlobY1 = useParallaxY(showcaseSectionRef, [-70, 70]);
  const showcaseBlobY2 = useParallaxY(showcaseSectionRef, [70, -70]);

  return (
    <motion.section
      ref={showcaseSectionRef}
      id="product-showcase"
      className="py-24 px-6 md:px-20 relative bg-white dark:bg-[#121212]"
      initial={{ opacity: 0 }}
      whileInView={{ opacity: 1 }}
      transition={{ duration: 0.8 }}
      viewport={{ once: false }}
    >
      <div className="absolute inset-0 z-0 pointer-events-none overflow-hidden">
        <motion.div className="absolute top-[-10%] left-[-5%] w-[40%] h-[60%] rounded-full bg-[#172b81] blur-[120px] opacity-[0.05]" style={{ y: showcaseBlobY1 }} />
        <motion.div className="absolute bottom-[-10%] right-[-5%] w-[40%] h-[60%] rounded-full bg-[#e1ae48] blur-[120px] opacity-[0.08]" style={{ y: showcaseBlobY2 }} />
      </div>

      <div className="max-w-screen-xl mx-auto relative z-10">
        <motion.div
          className="text-center max-w-2xl mx-auto mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: false }}
        >
          <p className="text-[10px] uppercase font-black text-[#172b81] dark:text-[#a8c0ff] mb-4 tracking-[0.5em]">
            Featured Lineup
          </p>
          <AnimatedText
            as={motion.h2}
            className="text-4xl sm:text-5xl font-black mb-4 text-slate-900 dark:text-white leading-tight tracking-tighter"
            text="A Freezer For Every Environment"
          />
          <p className="text-slate-500 dark:text-slate-400 font-light leading-relaxed">
            From compact retail displays to industrial-grade systems, explore the cold storage solutions our customers rely on most.
          </p>
        </motion.div>

        <motion.div
          className="grid grid-cols-1 md:grid-cols-5 gap-6"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: false, amount: 0.2 }}
          variants={{
            hidden: {},
            visible: { transition: { staggerChildren: 0.12, delayChildren: 0.1 } },
          }}
        >

          {/* 1. Liebherr Tech - redirects with Liebherr filter */}
          <motion.div
            variants={{ hidden: { opacity: 0, scale: 0.8 }, visible: { opacity: 1, scale: 1 } }}
            transition={{ type: "spring", stiffness: 260, damping: 20 }}
            whileHover={{ y: -5 }}
            className="md:col-span-3 h-[380px] relative overflow-hidden rounded-[40px] bg-[#F5F5F5] dark:bg-[#1E1E1E] border border-gray-200 dark:border-[#2C2C2C] p-12 flex flex-col justify-between group"
          >
            <div
              aria-hidden="true"
              className="pointer-events-none absolute inset-0 z-20 rounded-[40px] opacity-0 transition-opacity duration-500 group-hover:opacity-100"
              style={{
                padding: "2.5px 1px 1px 2.5px",
                background: "linear-gradient(135deg, rgba(225,174,72,0.45), rgba(255,255,255,0.1) 45%, rgba(23,43,129,0.45))",
                WebkitMask: "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
                WebkitMaskComposite: "xor",
                maskComposite: "exclude",
              }}
            />
            <div className="max-w-xs">
              <h3 className="text-3xl font-bold text-black dark:text-white tracking-tight mb-3">
                Liebherr Tech
              </h3>
              <p className="text-sm text-gray-500 dark:text-[#9CA3AF] leading-relaxed">
                Professional-grade refrigeration with integrated digital temperature monitoring and high-efficiency compressors.
              </p>
              <button
                type="button"
                onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    navigate('/products', { state: { filterBrand: 'liebherr' } });
                  }}
                className="mt-6 min-h-[40px] py-2 text-[10px] font-bold uppercase tracking-[0.3em] text-[#172b81] dark:text-[#e1ae48] flex items-center gap-2 transition-transform group-hover:translate-x-2"
              >
                Explore Collection <span>→</span>
              </button>
            </div>

            <div className="absolute right-[-10%] bottom-[-5%] w-[65%] flex items-center justify-center pointer-events-none">
              <div className="hidden dark:block absolute bottom-[-24px] w-[70%] h-[24px] bg-black/60 blur-2xl opacity-50" />
              <img
                src="./images/EFE 4602 PNG.webp"
                alt="Tech Showcase"
                loading="lazy"
                decoding="async"
                className="
                  relative w-full h-auto object-contain
                  transition-transform duration-700
                  group-hover:scale-105 group-hover:-rotate-2
                  dark:brightness-[0.96]
                  dark:contrast-[0.94]
                  dark:saturate-[0.92]
                  dark:drop-shadow-[0_28px_44px_rgba(0,0,0,0.45)]
                  dark:drop-shadow-[0_0_50px_rgba(23,43,129,0.18)]
                "
              />
            </div>
          </motion.div>

          {/* 2. Retail Gear - simple redirect */}
          <motion.div
            variants={{ hidden: { opacity: 0, scale: 0.8 }, visible: { opacity: 1, scale: 1 } }}
            transition={{ type: "spring", stiffness: 260, damping: 20 }}
            whileHover={{ y: -5 }}
            className="md:col-span-2 h-[380px] relative overflow-hidden rounded-[40px] bg-white dark:bg-[#1E1E1E] border border-gray-200 dark:border-[#2C2C2C] p-12 flex flex-col group"
          >
            <div
              aria-hidden="true"
              className="pointer-events-none absolute inset-0 z-20 rounded-[40px] opacity-0 transition-opacity duration-500 group-hover:opacity-100"
              style={{
                padding: "1px 2.5px 2.5px 1px",
                background: "linear-gradient(315deg, rgba(225,174,72,0.45), rgba(255,255,255,0.1) 45%, rgba(23,43,129,0.45))",
                WebkitMask: "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
                WebkitMaskComposite: "xor",
                maskComposite: "exclude",
              }}
            />
            <div>
              <h3 className="text-2xl font-bold text-black dark:text-white tracking-tight">
                Retail Gear
              </h3>
              <p className="text-xs text-gray-400 dark:text-gray-500 uppercase tracking-widest mt-1">
                Industrial Design
              </p>
            </div>

            <div className="flex-grow flex items-center justify-center relative pointer-events-none">
              <div className="hidden dark:block absolute bottom-[-20px] w-[65%] h-[22px] bg-black/60 blur-2xl opacity-50" />
              <img
                src="./images/Open Meat Display Chiller.webp"
                alt="Gear"
                loading="lazy"
                decoding="async"
                className="
                  relative w-[85%] h-auto object-contain
                  transition-all duration-700
                  group-hover:scale-110
                  grayscale group-hover:grayscale-0
                  dark:brightness-[0.96]
                  dark:contrast-[0.93]
                  dark:saturate-[0.9]
                  dark:drop-shadow-[0_26px_40px_rgba(0,0,0,0.45)]
                  dark:drop-shadow-[0_0_45px_rgba(225,174,72,0.16)]
                "
              />
            </div>

            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                navigate('/products');
              }}
              className="min-h-[40px] py-2 text-[10px] font-bold uppercase tracking-[0.3em] text-gray-400 dark:text-gray-500 hover:text-black dark:hover:text-white flex items-center gap-2"
            >
              View Details <span>→</span>
            </button>
          </motion.div>

          {/* 3. Island Units - simple redirect */}
          <motion.div
            variants={{ hidden: { opacity: 0, scale: 0.8 }, visible: { opacity: 1, scale: 1 } }}
            transition={{ type: "spring", stiffness: 260, damping: 20 }}
            whileHover={{ y: -5 }}
            className="md:col-span-2 h-[340px] relative overflow-hidden rounded-[40px] bg-white dark:bg-[#1E1E1E] border border-gray-200 dark:border-[#2C2C2C] p-12 flex flex-col justify-between group"
          >
            <div
              aria-hidden="true"
              className="pointer-events-none absolute inset-0 z-20 rounded-[40px] opacity-0 transition-opacity duration-500 group-hover:opacity-100"
              style={{
                padding: "2.5px 1px 1px 2.5px",
                background: "linear-gradient(135deg, rgba(23,43,129,0.45), rgba(255,255,255,0.1) 45%, rgba(225,174,72,0.45))",
                WebkitMask: "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
                WebkitMaskComposite: "xor",
                maskComposite: "exclude",
              }}
            />
            <div>
              <h3 className="text-2xl font-bold text-black dark:text-white tracking-tight">
                Island Units
              </h3>
              <p className="text-sm text-gray-500 dark:text-[#9CA3AF] mt-2">
                Maximum storage for wholesale environments.
              </p>
            </div>

            <div className="absolute bottom-[-20px] right-[-20px] w-40 h-40 rounded-full bg-[#e1ae48]/10 blur-2xl group-hover:bg-[#e1ae48]/20 transition-all duration-500" />

            <button
              type="button"
                onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                navigate('/products');
              }}
              className="min-h-[40px] py-2 text-[10px] font-bold uppercase tracking-[0.3em] text-gray-400 dark:text-gray-500 hover:text-[#172b81] flex items-center gap-2"
            >
              Browse Models <span>→</span>
            </button>
          </motion.div>

          {/* 4. The Cooling Experience - simple redirect */}
          <motion.div
            variants={{ hidden: { opacity: 0, scale: 0.8 }, visible: { opacity: 1, scale: 1 } }}
            transition={{ type: "spring", stiffness: 260, damping: 20 }}
            whileHover={{ y: -5 }}
            className="md:col-span-3 h-[340px] relative overflow-hidden rounded-[40px] bg-[#172b81] p-12 text-white group"
          >
            <div
              aria-hidden="true"
              className="pointer-events-none absolute inset-0 z-20 rounded-[40px] opacity-0 transition-opacity duration-500 group-hover:opacity-100"
              style={{
                padding: "1px 2.5px 2.5px 1px",
                background: "linear-gradient(315deg, rgba(225,174,72,0.4), rgba(255,255,255,0.08) 45%, rgba(255,255,255,0.35))",
                WebkitMask: "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
                WebkitMaskComposite: "xor",
                maskComposite: "exclude",
              }}
            />
            <div className="max-w-sm h-full flex flex-col justify-center relative z-10">
              <h3 className="text-4xl font-black tracking-tighter leading-none mb-4">
                The Cooling Experience.
              </h3>
              <p className="text-white/70 text-sm font-light leading-relaxed">
                Reliable hardware meets aesthetic excellence. Our curated selection of freezers is designed to fit seamlessly into modern retail and industrial spaces.
              </p>
              <CreativeButton
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  navigate('/products');
                }}
                fillColor="#e1ae48"
                hoverTextClassName="text-black"
                className="mt-8 bg-white text-[#172b81] px-6 py-2.5 min-h-[44px] text-[10px] font-bold uppercase tracking-widest w-fit transition-transform hover:scale-105 active:scale-95 shadow-lg"
              >
                Browse Products
              </CreativeButton>
            </div>

            <div className="absolute right-[-5%] top-[10%] text-[12rem] font-black text-white/[0.03] select-none leading-none pointer-events-none">
              COLD
            </div>
          </motion.div>

        </motion.div>
      </div>
    </motion.section>
  );
};

export default ProductShowcase;
